import { EditIcon } from "assets/icons";
import { useBoolean } from "hooks";
import { t } from "i18next";
import { useRefetchQuery } from "libs/reactQuery";
// Deep import (not the module barrel) to avoid a products <-> auditLog cycle.
import ShowAuditHistoryButton from "modules/auditLog/components/ShowAuditHistoryButton/ShowAuditHistoryButton";
import { StaffManagerPermissions, useProfile } from "modules/common";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Select } from "ui";

import { useChangeProductVariantVisibility } from "../../apis";
import { EProductVariantVisibility } from "../../constants";
import { DeleteProductModal } from "../Modals/DeleteProductModal";
import { ProductActionButtonsProps } from "./ProductActionButtons.types";

import styles from "./ProductActionButtons.module.scss";

const ProductActionButtons = ({ product }: ProductActionButtonsProps) => {
  const navigate = useNavigate();
  const { user } = useProfile({ enabled: false });

  const { canUpdate, canChangeVisibility, canDelete } = useMemo(() => {
    return {
      canUpdate:
        user.role === "Super" ||
        user.permissions?.includes(StaffManagerPermissions.UpdateProducts),
      canChangeVisibility:
        user.role === "Super" ||
        user.permissions?.includes(
          StaffManagerPermissions.ChangeProductVariantVisibilityForResellers
        ),
      canDelete:
        user.role === "Super" ||
        user.permissions?.includes(
          StaffManagerPermissions.DeleteProductVariants
        )
    };
  }, [user]);

  const refetchProducts = useRefetchQuery(["products"]);

  const changeProductVariantVisibility = useChangeProductVariantVisibility({
    onSuccess: refetchProducts
  });

  const { subChildCategoryId, productId, productVariantId } = useParams();

  const {
    value: isDeleteProductModalOpen,
    setTrue: openDeleteProductModal,
    setFalse: closeDeleteProductModal
  } = useBoolean(false);

  const navigateToProductEdit = () => {
    navigate(`/products/edit/${subChildCategoryId}/${productId}`, {
      state: { canGoBack: true }
    });
  };

  const navigateToProductsList = () => navigate("/products");

  const selectedVariant = useMemo(() => {
    return product.variants.find((variant) => variant.id === productVariantId);
  }, [product.variants, productVariantId]);

  return (
    <>
      <div className={styles.buttons}>
        {canUpdate && (
          <Button
            title={t("Edit")}
            variant="default"
            onClick={navigateToProductEdit}
            icon={<EditIcon fill={"#090914"} width={18} height={18} />}
          />
        )}
        {selectedVariant && canChangeVisibility && (
          <div className={styles.visibilitySelect}>
            <Select
              value={selectedVariant.visibility}
              options={[
                {
                  label: t("Visible"),
                  value: EProductVariantVisibility.Visible
                },
                {
                  label: t("Pack Only"),
                  value: EProductVariantVisibility.PackOnly
                },
                { label: t("Hidden"), value: EProductVariantVisibility.Hidden }
              ]}
              onChange={(next) => {
                const picked = Array.isArray(next) ? next[0] : next;
                if (
                  picked &&
                  picked !== selectedVariant.visibility &&
                  Object.values(EProductVariantVisibility).includes(
                    picked as EProductVariantVisibility
                  )
                ) {
                  changeProductVariantVisibility.mutate({
                    id: String(productVariantId),
                    visibility: picked as EProductVariantVisibility
                  });
                }
              }}
            />
          </div>
        )}
        {canDelete && (
          <Button
            title={t("Delete")}
            variant={"delete"}
            onClick={openDeleteProductModal}
          />
        )}

        <ShowAuditHistoryButton
          entityType="ProductVariant"
          entityId={productVariantId}
        />
      </div>

      <DeleteProductModal
        id={String(productVariantId)}
        isOpen={isDeleteProductModalOpen}
        onClose={closeDeleteProductModal}
        onDeleteSuccess={navigateToProductsList}
      />
    </>
  );
};

export default ProductActionButtons;
